import { setDeep } from "Libs/objectAccess";
import { roundedNumber } from "Libs/utils";

import type {
  ResourceTypeOptions,
  ResourceType,
  ProfileType,
  CpuRamOptions,
  InstancesOptions,
  ResourceData
} from "./types";

export const trialResourceLimits = {
  cpu: 4.5,
  ram: 12,
  disk: 20,
  project: 1,
  environment: 2
} as const;

export const getResourceType = (
  profileSize: string | null
): ResourceTypeOptions =>
  /\.gc$/.test(profileSize || "") ? "guaranteed" : "shared";

export const splitProfilesHandler = (profiles: ProfileType | undefined) =>
  Object.entries(profiles ?? {}).reduce<{
    guaranteed: ProfileType;
    shared: ProfileType;
  }>(
    (base, [key, value]) => {
      setDeep(base, [value.cpu_type, key], value);

      return base;
    },
    { guaranteed: {}, shared: {} }
  );

export const typeOptions = [
  {
    value: "guaranteed",
    label: "Guaranteed"
  },
  {
    value: "shared",
    label: "Shared"
  }
];

export const getDisabledCpuRamHandler = (
  v: ProfileType[string],
  minimumCpu: CpuRamOptions["minimumCpu"],
  minimumRam: CpuRamOptions["minimumRam"],
  isTrial: CpuRamOptions["isTrial"],
  instanceCount: CpuRamOptions["instanceCount"],
  currentCpu: CpuRamOptions["currentCpu"],
  currentRam: CpuRamOptions["currentRam"],
  totalValues: CpuRamOptions["totalValues"]
) => {
  if (v?.cpu < minimumCpu || v?.memory < minimumRam) {
    return true;
  } else if (isTrial) {
    const cpuCondition =
      totalValues.cpu.total -
        currentCpu * instanceCount +
        v?.cpu * instanceCount >
      trialResourceLimits.cpu;

    const ramCondition =
      totalValues.ram - currentRam * instanceCount + v?.memory * instanceCount >
      trialResourceLimits.ram * 1024;

    return cpuCondition || ramCondition;
  }

  return false;
};

export const getCpuRamOptions = ({
  profile,
  minimumCpu,
  minimumRam,
  isTrial,
  instanceCount,
  currentCpu,
  currentRam,
  totalValues
}: CpuRamOptions) =>
  Object.entries(profile)
    .sort(([, a], [, b]) => a?.cpu - b?.cpu)
    .map(([k, v]) => ({
      disabled: getDisabledCpuRamHandler(
        v,
        minimumCpu,
        minimumRam,
        isTrial,
        instanceCount,
        currentCpu,
        currentRam,
        totalValues
      ),
      value: k,
      label: `${v?.cpu} CPU, ${roundedNumber(v?.memory / 1024, 3)} GB RAM`
    }));

export const getInstancesOptions = ({
  instancesRange,
  isTrial,
  instanceCount,
  currentCpu,
  currentRam,
  totalValues
}: InstancesOptions) =>
  instancesRange.map(c => ({
    value: `${c}`,
    label: `${c}`,
    disabled:
      isTrial &&
      (totalValues.cpu.total - currentCpu * instanceCount + currentCpu * c >
        trialResourceLimits.cpu ||
        totalValues.ram - currentRam * instanceCount + currentRam * c >
          trialResourceLimits.ram * 1024)
  }));

export const defaultSelection = {
  value: "",
  label: "",
  disabled: false
};

export const calculateAppAndServices = (formState: ResourceData[]) => {
  const data = {
    guaranteedCpuApp: 0,
    guaranteedCpuServices: 0
  };

  formState.forEach(
    ({ profiles, profileSize, typeValue, resourceType, instanceCount }) => {
      const { cpu, memory } = profiles?.[typeValue][profileSize ?? ""] ?? {
        cpu: 0,
        memory: 0
      };

      if (typeValue === "guaranteed") {
        const instances = instanceCount ?? 1;
        if (["webapps", "workers"].includes(resourceType)) {
          data.guaranteedCpuApp += cpu * instances;
        } else {
          data.guaranteedCpuServices += memory * instances;
        }
      }
    }
  );

  return data;
};

export const orderResourcesHandler = (
  newFormState: Record<ResourceType, ResourceData[]>
) => {
  const { webapps, workers, services } = newFormState;

  const unclaimedWorkers: typeof workers = [];
  const webappsWithWorkers = [...webapps];
  workers.forEach(worker => {
    const webappLabel = worker.label.split("--")[0];

    let indexToPlaceWorker = -1;
    for (let i = webappsWithWorkers.length - 1; i >= 0; i--) {
      if (webappsWithWorkers[i].label.startsWith(webappLabel)) {
        indexToPlaceWorker = i;
        break;
      }
    }

    if (indexToPlaceWorker > -1) {
      webappsWithWorkers.splice(indexToPlaceWorker + 1, 0, worker);
    } else {
      unclaimedWorkers.push(worker);
    }
  });

  return [...webappsWithWorkers, ...unclaimedWorkers, ...services];
};
